import { useMemo, useRef, useState } from 'react';
import { UMIcon, UMMap, UMMapControls, UMR, UMSidePanel, type UMMapHandle } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { fmt } from '../proposition-c/ui';
import { recalculate } from './hooks';
import type { Bootstrap, BuildingConsumption, PlanProject } from './types';
import { Row } from './ui';

const heatColor = ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]];

function layers(pickedId: number | null) {
  return [
    { id: 'pj-buildings', type: 'fill' as const, source: 'buildings', paint: { 'fill-color': heatColor as never, 'fill-opacity': 0.85 } },
    { id: 'pj-pipes', type: 'line' as const, source: 'pipes', paint: { 'line-color': UMR.map.pipe, 'line-width': UMR.map.pipeWidth } },
    { id: 'pj-perimeter', type: 'line' as const, source: 'perimeter', paint: { 'line-color': PLAN.map.perimeter, 'line-width': 2.5 } },
    {
      id: 'pj-picked', type: 'line' as const, source: 'buildings',
      filter: ['==', ['get', 'id'], pickedId ?? -1] as never,
      paint: { 'line-color': UMR.map.picked, 'line-width': 3.5 },
    },
  ];
}

const LEGEND = [
  { label: '< 300 MWh/an', color: UMR.map.consumption[0] },
  { label: '300 – 600', color: UMR.map.consumption[1] },
  { label: '600 – 1 000', color: UMR.map.consumption[2] },
  { label: '> 1 000', color: UMR.map.consumption[3] },
];

/** Map of the project: buildings carry the consumption recalculated with the AO hypotheses. */
export function ProjectMap({ data, project }: { data: Bootstrap; project: PlanProject }) {
  const mapRef = useRef<UMMapHandle>(null);
  const [pickedId, setPickedId] = useState<number | null>(null);

  const rows = useMemo(() => recalculate(data.geo, project.params), [data.geo, project.params]);
  const byId = useMemo(() => {
    const m = new Map<string, BuildingConsumption>();
    rows.forEach((r) => m.set(r.functionalId, r));
    return m;
  }, [rows]);

  const sources = useMemo(() => ({
    buildings: {
      ...data.geo.buildings,
      features: data.geo.buildings.features.map((f) => {
        const fid = String(f.properties?.functionalId ?? '');
        return { ...f, properties: { ...f.properties, heat: byId.get(fid)?.planHeat ?? f.properties?.heat } };
      }),
    } as GeoJSON.FeatureCollection,
    pipes: data.geo.pipes,
    perimeter: data.geo.perimeter,
  }), [data.geo, byId]);

  const picked = useMemo(() => {
    const f = data.geo.buildings.features.find((x) => x.properties?.id === pickedId);
    return f ? byId.get(String(f.properties?.functionalId ?? '')) ?? null : null;
  }, [data.geo, byId, pickedId]);

  return (
    <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
      <UMMap
        ref={mapRef}
        sources={sources}
        layers={layers(pickedId)}
        initialBounds={data.geo.bounds}
        interactiveLayerIds={['pj-buildings']}
        onFeatureClick={(_layerId, f) => setPickedId((f?.properties?.id as number) ?? null)}
        onMapClick={() => setPickedId(null)}
      />
      <UMMapControls basemap="light" availableBasemaps={['light']} onZoomIn={() => mapRef.current?.zoomIn()} onZoomOut={() => mapRef.current?.zoomOut()} />

      <div
        style={{
          position: 'absolute', left: 16, bottom: 16, zIndex: 15, background: UMR.white, border: `1px solid ${UMR.border}`,
          padding: 10, fontFamily: UMR.font, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4,
        }}
      >
        <div style={{ fontWeight: 700 }}>Consommation du projet</div>
        {LEGEND.map((l) => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, background: l.color, display: 'inline-block' }} />
            {l.label}
          </div>
        ))}
      </div>

      {picked && (
        <UMSidePanel side="right" title="Fiche bâtiment du projet" onClose={() => setPickedId(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, fontFamily: UMR.font }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{picked.address}</div>
            <Row label="Identifiant fonctionnel UM" value={picked.functionalId} />
            <Row label="Usage" value={picked.usageLabel} />
            <Row label="Surface totale" value={picked.totalArea ? `${fmt(picked.totalArea)} m²` : 'Non renseignée'} />
            <Row
              label="Consommation projet"
              value={<span style={{ color: PLAN.accentStrong }}>{fmt(picked.planHeat)} MWh/an</span>}
            />
            <Row label="Consommation UrbanMap" value={`${fmt(picked.umHeat)} MWh/an`} />
            <div style={{ display: 'flex', gap: 8, padding: 10, background: PLAN.accentBg, border: `1px solid ${PLAN.accentBorder}`, fontSize: 12, color: PLAN.accentStrong }}>
              <UMIcon name="gavel" size={16} color={PLAN.accentStrong} />
              <div>
                Écart dû aux hypothèses de l’AO : station {project.params.weatherStation}, {project.params.weatherYear},
                ratio {picked.usageLabel.startsWith('Habitat') ? project.params.ratioResidential : project.params.ratioTertiary} %.
              </div>
            </div>
            <Row label="Source de la donnée" value={picked.measured ? 'Points de livraison SNCU (mesurée)' : data.lineage.source.split(' — ')[0]} />
            <Row label="Version du référentiel" value={data.lineage.version} />
            <Row label="Date d’extraction" value={project.createdAt} />
          </div>
        </UMSidePanel>
      )}
    </div>
  );
}
